import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, List, ListItem, Stack, Typography } from "@mui/material";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import { getToken } from "../services/auth";
import { ErrorState, LoadingState, RefreshButton } from "./AsyncViewState";

const announcementsApi = () => axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/api/messages/announcements`,
  headers: { Authorization: `Bearer ${getToken()}` },
});

export default function AnnouncementPanel({ open, onClose, onUnreadChange }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await announcementsApi().get("/");
      const readAt = new Date().toISOString();
      if (data.some((announcement) => !announcement.readAt)) {
        await announcementsApi().post("/read-all");
      }
      setAnnouncements(data.map((announcement) => ({ ...announcement, readAt: announcement.readAt || readAt })));
      onUnreadChange?.(0);
    } catch (loadError) {
      setError(loadError.response?.data?.error || "An error occurred while loading announcements");
    } finally {
      setLoading(false);
    }
  }, [onUnreadChange]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <CampaignRoundedIcon color="secondary" />
          <Box><Typography variant="h6" sx={{ fontWeight: 800 }}>Announcements</Typography><Typography variant="caption" color="text.secondary">Announcements remain available for 72 hours.</Typography></Box>
        </Stack>
        <RefreshButton onClick={load} loading={loading} label="Refresh announcements" />
      </Stack>
    </DialogTitle>
    <DialogContent dividers sx={{ p: 0, minHeight: 320 }}>
      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
      {loading ? <LoadingState label="Loading announcements..." /> : error && !announcements.length ? <ErrorState onRetry={load} message={error} /> : !announcements.length
        ? <Box sx={{ py: 10, textAlign: "center", color: "text.secondary" }}><CampaignRoundedIcon sx={{ fontSize: 46, opacity: .35 }} /><Typography sx={{ mt: 1 }}>No active announcements.</Typography></Box>
        : <List disablePadding>{announcements.map((announcement, index) => <React.Fragment key={announcement.id}>
          <ListItem alignItems="flex-start" sx={{ px: 3, py: 2 }}>
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={.5}>
                <Typography sx={{ fontWeight: 800 }}>{announcement.senderName}</Typography>
                <Typography variant="caption" color="text.secondary">{new Date(announcement.createdAt).toLocaleString()}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{announcement.body}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.25 }}>Expires {new Date(announcement.expiresAt).toLocaleString()}</Typography>
            </Box>
          </ListItem>
          {index < announcements.length - 1 && <Divider />}
        </React.Fragment>)}</List>}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>;
}
